import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function parseVCard(text) {
  const cards = text.split('BEGIN:VCARD').filter(c => c.trim());
  const contacts = [];
  for (const card of cards) {
    const lines = card.split('\n').map(l => l.trim());
    let firstName = '', lastName = '', birthday = null, phone = '', email = '';
    for (const line of lines) {
      if (line.startsWith('N:') || line.startsWith('N;')) {
        const val = line.split(':').slice(1).join(':');
        const parts = val.split(';');
        lastName = parts[0] || '';
        firstName = parts[1] || '';
      }
      if (line.startsWith('FN:') && !firstName) {
        const fullName = line.replace('FN:', '').trim();
        const parts = fullName.split(' ');
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }
      if (line.startsWith('BDAY:')) {
        const val = line.replace('BDAY:', '').replace(/-/g, '');
        if (val.length === 8) {
          birthday = `${val.slice(0, 4)}-${val.slice(4, 6)}-${val.slice(6, 8)}`;
        } else if (val.length >= 8) {
          const clean = val.replace(/[^0-9]/g, '');
          if (clean.length >= 8) {
            birthday = `${clean.slice(0, 4)}-${clean.slice(4, 6)}-${clean.slice(6, 8)}`;
          }
        }
      }
      if (line.startsWith('TEL')) phone = line.split(':').pop().trim();
      if (line.startsWith('EMAIL')) email = line.split(':').pop().trim();
    }
    if (firstName || lastName) {
      contacts.push({ first_name: firstName.trim(), last_name: lastName.trim(), birthday, phone, email, notify_pref: 'both' });
    }
  }
  contacts.sort((a, b) => {
    if (a.birthday && !b.birthday) return -1;
    if (!a.birthday && b.birthday) return 1;
    return (a.first_name || '').localeCompare(b.first_name || '');
  });
  return contacts;
}

function parseCSV(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l);
  if (lines.length < 2) return [];
  const header = lines[0].split(',').map(h => h.trim().toLowerCase());
  const firstIdx = header.findIndex(h => h.includes('first'));
  const lastIdx = header.findIndex(h => h.includes('last'));
  const relIdx = header.findIndex(h => h.includes('relationship'));
  const bdayIdx = header.findIndex(h => h.includes('birthday'));
  const annivIdx = header.findIndex(h => h.includes('anniversary'));
  const urlIdx = header.findIndex(h => h.includes('url') || h.includes('gift'));
  const contacts = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].match(/(".*?"|[^,]*),?/g)?.map(c => c.replace(/,\s*$/, '').replace(/^"|"$/g, '').trim()) || [];
    const firstName = firstIdx >= 0 ? (cols[firstIdx] || '') : '';
    const lastName = lastIdx >= 0 ? (cols[lastIdx] || '') : '';
    if (!firstName && !lastName) continue;
    let birthday = bdayIdx >= 0 ? (cols[bdayIdx] || '') : '';
    if (birthday && !/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
      const d = new Date(birthday);
      if (!isNaN(d)) birthday = d.toISOString().slice(0, 10);
      else birthday = '';
    }
    let anniversary = annivIdx >= 0 ? (cols[annivIdx] || '') : '';
    if (anniversary && !/^\d{4}-\d{2}-\d{2}$/.test(anniversary)) {
      const d = new Date(anniversary);
      if (!isNaN(d)) anniversary = d.toISOString().slice(0, 10);
      else anniversary = '';
    }
    contacts.push({
      first_name: firstName, last_name: lastName,
      relationship: relIdx >= 0 ? (cols[relIdx] || '') : '',
      birthday: birthday || null, anniversary: anniversary || null,
      gift_url: urlIdx >= 0 ? (cols[urlIdx] || '') : '',
      phone: '', email: '', notify_pref: 'both'
    });
  }
  return contacts;
}

const GUIDED_STEPS = [
  {
    title: 'Start with family',
    subtitle: 'Who in your family do you never want to forget?',
    prompts: [
      { label: 'Mom', relationship: 'Mom' },
      { label: 'Dad', relationship: 'Dad' },
      { label: 'Sibling', relationship: '', subOptions: ['Brother', 'Sister', 'Sibling'] },
      { label: 'Spouse / Partner', relationship: '', subOptions: ['Wife', 'Husband', 'Partner', 'Girlfriend', 'Boyfriend'] },
      { label: 'Child', relationship: 'Child' },
      { label: 'Extended family', relationship: '', subOptions: ['Aunt', 'Uncle', 'Brother-in-law', 'Sister-in-law'] },
      { label: 'Other', relationship: 'Other', isCustom: true },
    ]
  },
  {
    title: 'Your closest friends',
    subtitle: 'Think about the 2-3 friends you text or talk to most. Whose birthday would you hate to miss?',
    prompts: [],
    defaultRelationship: 'Friend'
  },
  {
    title: 'Someone to reconnect with',
    subtitle: 'Is there a childhood friend, old roommate, or mentor you\'d like to stay in better touch with? A birthday text goes a long way.',
    prompts: [],
    defaultRelationship: 'Friend'
  }
];

const PRESET_HOLIDAYS = [
  { name: "Mother's Day", month: null, day: null, preselected: true },
  { name: "Father's Day", month: null, day: null, preselected: true },
  { name: "Christmas", month: 12, day: 25, preselected: true },
  { name: "Valentine's Day", month: 2, day: 14, preselected: true },
  { name: "Thanksgiving", month: null, day: null, preselected: false },
  { name: "Hanukkah", month: 12, day: 7, preselected: false },
  { name: "Lunar New Year", month: 1, day: 29, preselected: false },
  { name: "Easter", month: null, day: null, preselected: false },
  { name: "Diwali", month: 10, day: 20, preselected: false },
];

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (year <= 1) return `${months[month]} ${day}`;
  return `${months[month]} ${day}, ${year}`;
}

function daysInMonth(month, year) {
  if (!month) return 31;
  return new Date(parseInt(year, 10) || 2000, parseInt(month, 10), 0).getDate();
}

function isValidDate(month, day, year) {
  if (!month && !day) return true;
  if ((month && !day) || (!month && day)) return false;
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  const maxDay = daysInMonth(m, year || 2000);
  if (d < 1 || d > maxDay) return false;
  if (year && year.length > 0 && year.length !== 4) return false;
  if (year && year.length === 4) {
    const y = parseInt(year, 10);
    const today = new Date();
    if (y < 1900) return false;
    if (y > today.getFullYear()) return false;
    if (y === today.getFullYear()) {
      const entered = new Date(y, m - 1, d);
      if (entered > today) return false;
    }
  }
  return true;
}

function getNextUpcoming(contacts) {
  const now = new Date();
  let closest = null;
  let closestDays = Infinity;
  let closestName = '';

  for (const c of contacts) {
    if (!c.birthday) continue;
    const parts = c.birthday.split('-');
    if (parts.length < 3) continue;
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const next = new Date(now.getFullYear(), month, day);
    if (next < now && next.toDateString() !== now.toDateString()) {
      next.setFullYear(now.getFullYear() + 1);
    }
    const diff = Math.ceil((next - now) / (1000 * 60 * 60 * 24));
    const actualDiff = next.toDateString() === now.toDateString() ? 0 : diff;
    if (actualDiff < closestDays) {
      closestDays = actualDiff;
      closest = next;
      closestName = c.first_name + (c.last_name ? ' ' + c.last_name : '');
    }
  }

  if (!closest) return null;
  return {
    name: closestName,
    date: closest.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    days: closestDays
  };
}

export default function Onboarding({ userId, demo = false }) {
  const [showSplash, setShowSplash] = useState(true);
  const [mode, setMode] = useState('guided');
  const [guidedStep, setGuidedStep] = useState(0);
  const [addedContacts, setAddedContacts] = useState([]);
  const [form, setForm] = useState({ first_name: '', last_name: '', birthday_month: '', birthday_day: '', birthday_year: '', relationship: '', custom_relationship: '', notify_pref: 'both' });
  const [showAnniversary, setShowAnniversary] = useState(false);
  const [anniversary, setAnniversary] = useState('');
  const [annivMonth, setAnnivMonth] = useState('');
  const [annivDay, setAnnivDay] = useState('');
  const [annivYear, setAnnivYear] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [done, setDone] = useState(false);
  const [expandedPrompt, setExpandedPrompt] = useState(null);
  const [skipBirthday, setSkipBirthday] = useState(false);
  const [userNotifyPref, setUserNotifyPref] = useState('both');

  // Holiday step
  const [holidayStep, setHolidayStep] = useState(false);
  const [selectedHolidays, setSelectedHolidays] = useState(
    PRESET_HOLIDAYS.filter(h => h.preselected).map(h => ({ ...h }))
  );
  const [customHolidayName, setCustomHolidayName] = useState('');
  const [customHolidayMonth, setCustomHolidayMonth] = useState('');
  const [customHolidayDay, setCustomHolidayDay] = useState('');
  const [savingHolidays, setSavingHolidays] = useState(false);

  // File import
  const [fileContacts, setFileContacts] = useState([]);
  const [fileStep, setFileStep] = useState(1);
  const [importing, setImporting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      fetch(`/api/users/${userId}`)
        .then(r => r.json())
        .then(user => { if (user && user.notify_pref) setUserNotifyPref(user.notify_pref); })
        .catch(() => {});
    }
  }, [userId]);

  useEffect(() => {
    setForm(prev => ({ ...prev, notify_pref: userNotifyPref }));
  }, [userNotifyPref]);

  if (!userId && !demo) return <p>Please sign up first.</p>;

  const currentGuide = GUIDED_STEPS[guidedStep];

  const buildBirthdayString = () => {
    if (!form.birthday_month || !form.birthday_day) return null;
    const year = form.birthday_year && form.birthday_year.length === 4 ? form.birthday_year : '0001';
    return `${year}-${form.birthday_month.padStart(2, '0')}-${form.birthday_day.padStart(2, '0')}`;
  };

  const resetForm = () => {
    setForm({ first_name: '', last_name: '', birthday_month: '', birthday_day: '', birthday_year: '', relationship: '', custom_relationship: '', notify_pref: userNotifyPref });
    setShowAnniversary(false);
    setAnniversary('');
    setAnnivMonth(''); setAnnivDay(''); setAnnivYear('');
    setSkipBirthday(false);
    setExpandedPrompt(null);
    setMsg(null);
  };

  const selectPrompt = (prompt) => {
    const alreadyActive = form.relationship === prompt.relationship && !prompt.isCustom;
    if (alreadyActive) {
      setForm({ ...form, relationship: '', custom_relationship: '' });
    } else {
      setForm({ ...form, relationship: prompt.relationship, custom_relationship: '' });
    }
    setShowAnniversary(false);
    setAnniversary('');
    setAnnivMonth(''); setAnnivDay(''); setAnnivYear('');
    setSkipBirthday(false);
  };

  const effectiveRelationship = () => {
    if (form.relationship === 'Other') return form.custom_relationship.trim() || 'Other';
    return form.relationship || currentGuide.defaultRelationship || '';
  };

  const saveContact = async () => {
    const rel = effectiveRelationship();
    if (!form.first_name.trim() && (guidedStep > 0 || !rel)) {
      setMsg(guidedStep > 0 ? 'Please enter a first name.' : 'Please enter a name or select a relationship.');
      return;
    }
    if (!skipBirthday && !isValidDate(form.birthday_month, form.birthday_day, form.birthday_year)) {
      setMsg('That birthday doesn\'t look right — please check the date.');
      return;
    }
    if (showAnniversary && !isValidDate(annivMonth, annivDay, annivYear)) {
      setMsg('That anniversary doesn\'t look right — please check the date.');
      return;
    }
    const firstName = form.first_name.trim() || rel;
    setSaving(true);
    setMsg(null);
    const birthday = skipBirthday ? null : buildBirthdayString();
    const needsBirthdayLater = skipBirthday || !birthday;
    const annivStr = showAnniversary && annivMonth && annivDay
      ? `${annivYear && annivYear.length === 4 ? annivYear : '0001'}-${annivMonth.padStart(2,'0')}-${annivDay.padStart(2,'0')}`
      : null;
    try {
      if (!demo) {
        const res = await fetch('/api/contacts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            first_name: firstName,
            last_name: form.last_name.trim() || null,
            birthday: birthday,
            anniversary: annivStr,
            relationship: rel || null,
            notes: needsBirthdayLater ? 'needs_birthday' : null,
            notify_pref: form.notify_pref || userNotifyPref
          })
        });
        if (res.status === 403) {
          setMsg('UPGRADE');
          setSaving(false);
          return;
        }
        if (res.status === 401) {
          localStorage.removeItem('string_user_id');
          navigate('/signup?session_expired=1');
          return;
        }
        if (!res.ok) {
          setMsg('Something went wrong. Please try again.');
          setSaving(false);
          return;
        }
      }
      setAddedContacts(prev => [...prev, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        relationship: rel,
        birthday: birthday,
        needsBirthday: needsBirthdayLater
      }]);
      resetForm();
    } catch {
      setMsg('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const nextGuidedStep = () => {
    if (guidedStep < GUIDED_STEPS.length - 1) {
      setGuidedStep(guidedStep + 1);
      resetForm();
    } else {
      if (addedContacts.length === 0) {
        setMsg('Please add at least one person before continuing.');
        return;
      }
      setHolidayStep(true);
    }
  };

  const finishOnboarding = async () => {
    if (addedContacts.length === 0) {
      setMsg('Please add at least one person before finishing.');
      setHolidayStep(false);
      return;
    }
    if (!demo && userId) {
      setSavingHolidays(true);
      try {
        await fetch(`/api/holidays/user/${userId}/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ holidays: selectedHolidays.map(h => ({ name: h.name, month: h.month || null, day: h.day || null })) })
        });
      } catch {}
      setSavingHolidays(false);
    }

    const contactsNeedingBirthdays = addedContacts.filter(c => c.needsBirthday);
    if (contactsNeedingBirthdays.length > 0 && !demo && userId) {
      try {
        await fetch('/api/users/' + userId + '/schedule-birthday-reminder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ names: contactsNeedingBirthdays.map(c => c.first_name + (c.last_name ? ' ' + c.last_name : '')) })
        });
      } catch {}
    }
    if (!demo && userId) {
      try {
        await fetch('/api/users/' + userId + '/welcome', { method: 'POST' });
      } catch {}
    }
    setDone(true);
  };

  const toggleHoliday = (holiday) => {
    const exists = selectedHolidays.find(h => h.name === holiday.name);
    if (exists) {
      setSelectedHolidays(selectedHolidays.filter(h => h.name !== holiday.name));
    } else {
      if (selectedHolidays.length >= 7) return;
      setSelectedHolidays([...selectedHolidays, { ...holiday }]);
    }
  };

  const addCustomHoliday = () => {
    if (!customHolidayName.trim()) return;
    if (selectedHolidays.length >= 7) return;
    const existing = selectedHolidays.find(h => h.name.toLowerCase() === customHolidayName.trim().toLowerCase());
    if (existing) return;
    setSelectedHolidays([...selectedHolidays, {
      name: customHolidayName.trim(),
      month: customHolidayMonth ? parseInt(customHolidayMonth, 10) : null,
      day: customHolidayDay ? parseInt(customHolidayDay, 10) : null,
      preselected: false
    }]);
    setCustomHolidayName('');
    setCustomHolidayMonth('');
    setCustomHolidayDay('');
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const name = file.name.toLowerCase();
    let parsed = [];
    if (name.endsWith('.vcf')) parsed = parseVCard(text);
    else if (name.endsWith('.csv')) parsed = parseCSV(text);
    else { setMsg('Please upload a .vcf or .csv file.'); return; }
    if (parsed.length === 0) { setMsg('No contacts found in this file.'); return; }
    if (parsed.length > 25) {
      setFileContacts(parsed.slice(0, 25));
      setMsg(`Found ${parsed.length} contacts — loaded the first 25 (free plan limit).`);
    } else {
      setFileContacts(parsed);
      setMsg(null);
    }
    setFileStep(2);
  };

  const setContactPref = (index, pref) => {
    setFileContacts(prev => prev.map((c, i) => i === index ? { ...c, notify_pref: pref } : c));
  };

  const setAllPref = (pref) => {
    setFileContacts(prev => prev.map(c => ({ ...c, notify_pref: pref })));
  };

  const importFileContacts = async () => {
    if (fileContacts.length === 0) return;
    setImporting(true);
    setMsg(null);
    try {
      let imported = 0;
      let hitLimit = false;
      if (demo) {
        await new Promise(r => setTimeout(r, 800));
        imported = fileContacts.length;
      } else {
        for (const c of fileContacts) {
          const res = await fetch('/api/contacts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              first_name: c.first_name, last_name: c.last_name || null,
              birthday: c.birthday || null, relationship: c.relationship || null,
              anniversary: c.anniversary || null, notes: null,
              notify_pref: c.notify_pref || 'both'
            })
          });
          if (res.ok) {
            imported++;
            if (c.gift_url) {
              const data = await res.json();
              await fetch('/api/contacts/' + data.id + '/gifts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idea: 'Gift idea', url: c.gift_url })
              });
            }
          } else if (res.status === 403) {
            hitLimit = true;
            break;
          }
        }
      }
      setAddedContacts(prev => [...prev, ...fileContacts.slice(0, imported).map(c => ({
        first_name: c.first_name, last_name: c.last_name, relationship: c.relationship, birthday: c.birthday
      }))]);
      setDone(true);
      if (hitLimit) {
        setMsg(`Imported ${imported} contact${imported !== 1 ? 's' : ''} — hit the free plan limit. UPGRADE`);
      } else {
        setMsg(`Imported ${imported} contact${imported !== 1 ? 's' : ''}!`);
      }
    } catch {
      setMsg('Something went wrong during import.');
    } finally {
      setImporting(false);
    }
  };

  const nextEvent = getNextUpcoming(addedContacts);

  // ─── Done screen ───────────────────────────────────────────────────────────
  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎉</div>
        <h2 style={{ color: '#5D4037', marginBottom: '0.5rem' }}>You're all set!</h2>
        <p style={{ color: '#666', marginBottom: '0.25rem', fontSize: '1.1rem' }}>
          You added <strong>{addedContacts.length}</strong> contact{addedContacts.length !== 1 ? 's' : ''} to String.
        </p>
        {selectedHolidays.length > 0 && (
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
            Tracking {selectedHolidays.length} holiday{selectedHolidays.length !== 1 ? 's' : ''}: {selectedHolidays.map(h => h.name).join(', ')}.
          </p>
        )}

        {nextEvent && (
          <div className="card" style={{ textAlign: 'center', margin: '1.25rem auto', maxWidth: '400px', background: '#f9f6f2' }}>
            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Your next reminder</p>
            <p style={{ color: '#5D4037', fontSize: '1.15rem', fontWeight: '600', marginBottom: '0.15rem' }}>
              {nextEvent.name}'s birthday
            </p>
            <p style={{ color: '#666', fontSize: '0.95rem' }}>
              {nextEvent.date}
              {nextEvent.days === 0 ? ' (today!)' : nextEvent.days === 1 ? ' (tomorrow!)' : ` (${nextEvent.days} days)`}
            </p>
          </div>
        )}

        {addedContacts.some(c => c.needsBirthday) && (
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem', lineHeight: '1.5' }}>
            We'll text you in a few days to help fill in the birthdays you skipped.
          </p>
        )}

        <p style={{ color: '#888', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.9rem' }}>
          We'll send you reminders as each event approaches so you never forget.
          You can always add more contacts later or text us anytime.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {demo ? (
            <button onClick={() => { setDone(false); setGuidedStep(0); setAddedContacts([]); setMode('guided'); setShowSplash(true); setHolidayStep(false); }}>Run Again</button>
          ) : (
            <button onClick={() => navigate('/account')} style={{ background: '#5D4037', fontSize: '1rem', padding: '0.75rem 2rem' }}>View My Contacts</button>
          )}
        </div>
      </div>
    );
  }

  // ─── File import mode ──────────────────────────────────────────────────────
  if (mode === 'file') {
    return (
      <div>
        {fileStep === 1 && (
          <div>
            <h2>Import contacts from a file</h2>
            <p style={{ color: '#666', marginBottom: '1rem', lineHeight: '1.6' }}>
              Upload a vCard (.vcf) from Apple Contacts or a CSV file from Google Sheets.
            </p>

            <div className="card" style={{ marginBottom: '1rem', cursor: 'pointer' }} onClick={() => document.getElementById('vcf-input').click()}>
              <h3>Apple Contacts (vCard)</h3>
              <ol style={{ fontSize: '0.85rem', color: '#888', lineHeight: '1.7', paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
                <li>Log in to <a href="https://www.icloud.com/contacts" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>iCloud.com</a> in your web browser</li>
                <li>Select <strong>Contacts</strong></li>
                <li>Create a new contact list called <strong>"String"</strong></li>
                <li>Search for the contacts you want and drag them into the list</li>
                <li>Once you have 15-20 contacts, click the <strong>Share icon</strong> (top right)</li>
                <li>Click <strong>Export vCard</strong></li>
                <li>Upload the file here</li>
              </ol>
              <input id="vcf-input" type="file" accept=".vcf" style={{ display: 'none' }} onChange={handleFile} />
              <button style={{ marginTop: '0.5rem', fontSize: '0.85rem', padding: '0.4rem 1rem' }}>Upload .vcf file</button>
            </div>

            <div className="card" style={{ marginBottom: '1rem', cursor: 'pointer' }} onClick={() => document.getElementById('csv-input').click()}>
              <h3>Google Sheets (CSV)</h3>
              <ol style={{ fontSize: '0.85rem', color: '#888', lineHeight: '1.7', paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
                <li>Make a copy of <a href="https://docs.google.com/spreadsheets/d/1n4u78UvEWXgAnANXABi8fqux3o20gH4Mk3FauInlbj8/edit" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>this Google Sheet template</a></li>
                <li>Fill in names, relationships, birthdays, and anniversaries</li>
                <li>Go to <strong>File → Download → CSV</strong></li>
                <li>Upload the file here</li>
              </ol>
              <input id="csv-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />
              <button style={{ marginTop: '0.5rem', fontSize: '0.85rem', padding: '0.4rem 1rem' }}>Upload .csv file</button>
            </div>

            {msg && <div className="msg err">{msg}</div>}
            <button onClick={() => { setMode('guided'); setShowSplash(true); }} style={{ background: 'transparent', color: '#888', fontSize: '0.85rem', padding: '0.5rem 0', textDecoration: 'underline' }}>Back to guided setup</button>
          </div>
        )}

        {fileStep === 2 && (
          <div>
            <h2>Set notification preferences</h2>
            <p style={{ color: '#666', marginBottom: '0.5rem' }}>
              We found <strong>{fileContacts.length}</strong> contacts. Choose how you'd like to be reminded about each person.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: '#888' }}>Set all to:</span>
              {['sms', 'email', 'both'].map(pref => (
                <button key={pref} onClick={() => setAllPref(pref)} style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem', background: '#666' }}>
                  {pref === 'sms' ? 'Text' : pref === 'email' ? 'Email' : 'Both'}
                </button>
              ))}
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '1rem' }}>
              {fileContacts.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', borderRadius: '4px', marginBottom: '2px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: '500' }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#888' }}>
                      {c.relationship && <span>{c.relationship}</span>}
                      {c.relationship && c.birthday && <span> · </span>}
                      {c.birthday && <span>Birthday: {c.birthday}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                    {['sms', 'email', 'both'].map(pref => (
                      <button key={pref} onClick={() => setContactPref(i, pref)} style={{
                        fontSize: '0.75rem', padding: '0.25rem 0.5rem',
                        background: (c.notify_pref || 'both') === pref ? '#2c2c2c' : '#ddd',
                        color: (c.notify_pref || 'both') === pref ? '#fff' : '#555',
                        borderRadius: '4px', border: 'none', cursor: 'pointer'
                      }}>
                        {pref === 'sms' ? 'Text' : pref === 'email' ? 'Email' : 'Both'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {msg && msg.endsWith('UPGRADE') ? (
              <div style={{ background: '#fdf6ee', border: '1px solid #d4a96a', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.85rem', color: '#5D4037', marginBottom: '0.4rem' }}>{msg.replace(/ UPGRADE$/, '')}</div>
                <div style={{ fontSize: '0.82rem', color: '#7a5c45', marginBottom: '0.6rem' }}>Upgrade to Premium for unlimited contacts — $20/year.</div>
                <a href="/upgrade" style={{ display: 'inline-block', background: '#5D4037', color: '#fff', padding: '0.45rem 1rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600', textDecoration: 'none' }}>Go Premium →</a>
              </div>
            ) : msg ? (
              <div className="msg err">{msg}</div>
            ) : null}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setFileStep(1)} style={{ background: '#999' }}>Back</button>
              <button onClick={importFileContacts} disabled={importing}>
                {importing ? 'Importing...' : `Import ${fileContacts.length} Contact${fileContacts.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Splash screen ─────────────────────────────────────────────────────────
  if (showSplash && !holidayStep) {
    return (
      <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>👨‍👩‍👧‍👦</div>
        <h2 style={{ color: '#6B7F5E', marginBottom: '0.75rem', fontSize: '1.6rem' }}>
          Let's start with your family
        </h2>
        <p style={{ color: '#666', lineHeight: '1.7', maxWidth: '380px', margin: '0 auto 2rem', fontSize: '1rem' }}>
          We'll begin with the people closest to you — parents, siblings, and partners.
          Then we'll move on to friends and others.
        </p>
        <button
          onClick={() => setShowSplash(false)}
          style={{ background: '#5D4037', color: '#fff', fontSize: '1.1rem', padding: '0.85rem 2.5rem', borderRadius: '10px', border: 'none', cursor: 'pointer' }}
        >
          Let's go →
        </button>
        <div style={{ marginTop: '2rem', borderTop: '1px solid #D7CCC8', paddingTop: '1.25rem' }}>
          <button onClick={() => { setShowSplash(false); setMode('file'); }} style={{ background: '#F5F0EB', color: '#5D4037', fontSize: '0.95rem', fontWeight: '600', padding: '0.65rem 1.1rem', border: '2px solid #5D4037', borderRadius: '8px', cursor: 'pointer' }}>
            Already have birthdays? Import from vCard or CSV instead
          </button>
        </div>
      </div>
    );
  }

  // ─── Holiday step ──────────────────────────────────────────────────────────
  if (holidayStep) {
    const allPresets = PRESET_HOLIDAYS;
    return (
      <div>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Final step — Holidays</span>
        </div>
        <div style={{ height: '4px', background: '#5D4037', borderRadius: '2px', marginBottom: '1.5rem' }} />

        <h2 style={{ marginBottom: '0.25rem', color: '#2c2c2c' }}>Your holiday reminders</h2>
        <p style={{ color: '#666', marginBottom: '1.25rem', lineHeight: '1.6' }}>
          The four main holidays are on by default — tap to remove any you don't need. Add others below.
        </p>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            {allPresets.map((h, i) => {
              const isSelected = selectedHolidays.some(s => s.name === h.name);
              const isDefault = h.preselected;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleHoliday(h)}
                  style={{
                    fontSize: '0.85rem', padding: '0.4rem 0.9rem',
                    background: isSelected ? '#5D4037' : '#f0ebe6',
                    color: isSelected ? '#fff' : '#5D4037',
                    borderRadius: '20px',
                    border: isDefault && !isSelected ? '1.5px dashed #5D4037' : 'none',
                    cursor: 'pointer', opacity: 1
                  }}
                >
                  {isSelected ? '✓ ' : ''}{h.name}
                </button>
              );
            })}
          </div>

          {selectedHolidays.filter(h => !PRESET_HOLIDAYS.find(p => p.name === h.name)).map((h, i) => (
            <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#5D4037', color: '#fff', borderRadius: '20px', padding: '0.4rem 0.9rem', fontSize: '0.85rem', marginRight: '0.5rem', marginBottom: '0.5rem' }}>
              {h.name} {h.month && h.day ? `(${h.month}/${h.day})` : ''}
              <button onClick={() => setSelectedHolidays(selectedHolidays.filter(s => s.name !== h.name))} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '0.9rem', padding: 0, lineHeight: 1 }}>✕</button>
            </div>
          ))}

          {selectedHolidays.length < 7 && (
            <div style={{ marginTop: '0.5rem' }}>
              <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '0.4rem' }}>+ Add a custom holiday</label>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <input
                  value={customHolidayName}
                  onChange={e => setCustomHolidayName(e.target.value)}
                  placeholder="Holiday name"
                  style={{ flex: 2, minWidth: '120px' }}
                />
                <select value={customHolidayMonth} onChange={e => setCustomHolidayMonth(e.target.value)} style={{ flex: 1, minWidth: '80px' }}>
                  <option value="">Month</option>
                  {MONTH_NAMES.map((m, i) => <option key={i} value={String(i + 1)}>{m}</option>)}
                </select>
                <input
                  value={customHolidayDay}
                  onChange={e => setCustomHolidayDay(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  placeholder="Day"
                  inputMode="numeric"
                  style={{ flex: 1, minWidth: '50px' }}
                />
                <button
                  type="button"
                  onClick={addCustomHoliday}
                  disabled={!customHolidayName.trim()}
                  style={{ background: '#6B7F5E', fontSize: '0.85rem', padding: '0.4rem 0.8rem', opacity: customHolidayName.trim() ? 1 : 0.5 }}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => setHolidayStep(false)} style={{ background: '#999', flex: 1 }}>Back</button>
          <button
            onClick={finishOnboarding}
            disabled={savingHolidays}
            style={{ background: '#5D4037', color: '#fff', flex: 2 }}
          >
            {savingHolidays ? 'Saving...' : 'Finish'}
          </button>
        </div>
      </div>
    );
  }

  // ─── Guided form ──────────────────────────────────────────────────────────
  const months = MONTH_NAMES;

  return (
    <div>
      {demo && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '0.5rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#856404' }}>
          Preview Mode — nothing will be saved
        </div>
      )}

      <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Step {guidedStep + 1} of {GUIDED_STEPS.length}</span>
        {addedContacts.length > 0 && (
          <span style={{ fontSize: '0.8rem', color: '#6B7F5E', fontWeight: '500' }}>
            {addedContacts.length} added
          </span>
        )}
      </div>

      <div style={{ height: '4px', background: '#e0e0e0', borderRadius: '2px', marginBottom: '1.5rem' }}>
        <div style={{ height: '100%', background: '#5D4037', borderRadius: '2px', width: `${((guidedStep + 1) / GUIDED_STEPS.length) * 100}%`, transition: 'width 0.3s ease' }} />
      </div>

      <h2 style={{ marginBottom: '0.25rem', color: guidedStep === 0 ? '#6B7F5E' : '#2c2c2c' }}>{currentGuide.title}</h2>
      <p style={{ color: '#666', marginBottom: '1.25rem', lineHeight: '1.6' }}>{currentGuide.subtitle}</p>

      {addedContacts.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {addedContacts.map((c, i) => {
            const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
            let chipLabel;
            if (!fullName) {
              chipLabel = c.relationship || 'Contact';
            } else if (c.relationship) {
              chipLabel = `${fullName} — ${c.relationship}`;
            } else {
              chipLabel = fullName;
            }
            return (
              <div key={i} style={{ display: 'inline-block', background: '#f0ebe6', borderRadius: '20px', padding: '0.3rem 0.75rem', fontSize: '0.85rem', color: '#5D4037', marginRight: '0.5rem', marginBottom: '0.4rem' }}>
                {chipLabel}
              </div>
            );
          })}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        {currentGuide.prompts.length > 0 && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {currentGuide.prompts.map((prompt, i) => {
                const isActive = prompt.subOptions
                  ? prompt.subOptions.includes(form.relationship)
                  : (form.relationship === prompt.relationship && prompt.relationship && !prompt.isCustom)
                    || (prompt.isCustom && form.relationship === 'Other');
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      if (prompt.subOptions) {
                        if (prompt.subOptions.includes(form.relationship)) {
                          setForm({ ...form, relationship: '', custom_relationship: '' });
                          setExpandedPrompt(null);
                        } else {
                          setExpandedPrompt(expandedPrompt === i ? null : i);
                        }
                      } else if (prompt.isCustom) {
                        if (form.relationship === 'Other') {
                          setForm({ ...form, relationship: '', custom_relationship: '' });
                        } else {
                          setForm({ ...form, relationship: 'Other', custom_relationship: '' });
                        }
                        setExpandedPrompt(null);
                      } else {
                        selectPrompt(prompt);
                        setExpandedPrompt(null);
                      }
                    }}
                    style={{
                      fontSize: '0.85rem',
                      padding: '0.4rem 0.9rem',
                      background: isActive ? '#5D4037' : '#f0ebe6',
                      color: isActive ? '#fff' : '#5D4037',
                      borderRadius: '20px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {prompt.label}
                  </button>
                );
              })}
            </div>
            {expandedPrompt !== null && currentGuide.prompts[expandedPrompt]?.subOptions && (
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem', paddingLeft: '0.25rem' }}>
                {currentGuide.prompts[expandedPrompt].subOptions.map((sub, j) => (
                  <button
                    key={j}
                    type="button"
                    onClick={() => {
                      if (form.relationship === sub) {
                        setForm({ ...form, relationship: '', custom_relationship: '' });
                      } else {
                        setForm({ ...form, relationship: sub, first_name: form.first_name || '', custom_relationship: '' });
                      }
                      setExpandedPrompt(null);
                    }}
                    style={{
                      fontSize: '0.8rem',
                      padding: '0.35rem 0.75rem',
                      background: form.relationship === sub ? '#5D4037' : '#fff',
                      color: form.relationship === sub ? '#fff' : '#5D4037',
                      borderRadius: '16px',
                      border: '1px solid #5D4037',
                      cursor: 'pointer'
                    }}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}
            {form.relationship === 'Other' && (
              <div style={{ marginTop: '0.5rem' }}>
                <input
                  value={form.custom_relationship}
                  onChange={e => setForm({ ...form, custom_relationship: e.target.value })}
                  placeholder="Describe the relationship (e.g. friend's child, neighbor)"
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>
        )}

        {guidedStep === 0 && (form.relationship || currentGuide.defaultRelationship) && form.relationship !== 'Other' && (
          <p style={{ fontSize: '0.8rem', color: '#6B7F5E', marginBottom: '0.3rem', marginTop: 0 }}>
            Name is optional — you can just add a birthdate for "{effectiveRelationship()}"
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <input
            value={form.first_name}
            onChange={e => setForm({ ...form, first_name: e.target.value })}
            placeholder={guidedStep === 0 && form.relationship ? "First name (optional)" : "First name"}
            style={{ flex: 1 }}
          />
          <input
            value={form.last_name}
            onChange={e => setForm({ ...form, last_name: e.target.value })}
            placeholder="Last name (optional)"
            style={{ flex: 1 }}
          />
        </div>

        <div style={{ marginBottom: '0.5rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '0.3rem' }}>
            Birthday <span style={{ color: '#bbb' }}>(year is optional)</span>
          </label>
          {!skipBirthday ? (
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <select
                value={form.birthday_month}
                onChange={e => {
                const nm = e.target.value;
                const maxDay = daysInMonth(nm, form.birthday_year);
                setForm({ ...form, birthday_month: nm, birthday_day: (form.birthday_day && parseInt(form.birthday_day) > maxDay) ? '' : form.birthday_day });
              }}
                style={{ flex: 2, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem', background: '#fff' }}
              >
                <option value="">Month</option>
                {months.map((m, i) => (
                  <option key={i} value={String(i + 1)}>{m}</option>
                ))}
              </select>
              <select
                value={form.birthday_day}
                onChange={e => setForm({ ...form, birthday_day: e.target.value })}
                style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem', background: '#fff' }}
              >
                <option value="">Day</option>
                {Array.from({ length: daysInMonth(form.birthday_month, form.birthday_year) }, (_, i) => (
                  <option key={i} value={String(i + 1)}>{i + 1}</option>
                ))}
              </select>
              <input
                value={form.birthday_year}
                onChange={e => setForm({ ...form, birthday_year: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                placeholder="Year"
                style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem', textAlign: 'center' }}
                inputMode="numeric"
              />
            </div>
          ) : (
            <div style={{ padding: '0.5rem', background: '#f9f6f2', borderRadius: '6px', fontSize: '0.85rem', color: '#888' }}>
              We'll remind you to add this later
            </div>
          )}
          <button
            type="button"
            onClick={() => { setSkipBirthday(!skipBirthday); if (!skipBirthday) setForm({ ...form, birthday_month: '', birthday_day: '', birthday_year: '' }); }}
            style={{ background: 'transparent', color: '#888', fontSize: '0.8rem', padding: '0.3rem 0', textDecoration: 'underline', border: 'none', cursor: 'pointer', marginTop: '0.3rem' }}
          >
            {skipBirthday ? 'Add birthdate now' : 'I\'ll add the birthdate later'}
          </button>
        </div>

        {!showAnniversary ? (
          <button
            type="button"
            onClick={() => setShowAnniversary(true)}
            style={{ background: 'transparent', color: '#5D4037', fontSize: '0.85rem', padding: '0.3rem 0', border: 'none', cursor: 'pointer', marginBottom: '0.75rem', display: 'block' }}
          >
            + Add anniversary
          </button>
        ) : (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
              <label style={{ fontSize: '0.8rem', color: '#888' }}>Anniversary <span style={{ color: '#bbb' }}>(year is optional)</span></label>
              <button
                type="button"
                onClick={() => { setShowAnniversary(false); setAnniversary(''); setAnnivMonth(''); setAnnivDay(''); setAnnivYear(''); }}
                style={{ background: 'transparent', color: '#999', fontSize: '0.8rem', padding: '0.3rem', border: 'none', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={annivMonth} onChange={e => { const nm = e.target.value; const maxDay = daysInMonth(nm, annivYear); setAnnivMonth(nm); if (annivDay && parseInt(annivDay) > maxDay) setAnnivDay(''); }} style={{ flex: 2, minWidth: 0, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem', background: '#fff' }}>
                <option value="">Month</option>
                {months.map((m, i) => <option key={i} value={String(i+1)}>{m}</option>)}
              </select>
              <select value={annivDay} onChange={e => setAnnivDay(e.target.value)} style={{ flex: 1, minWidth: 0, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem', background: '#fff' }}>
                <option value="">Day</option>
                {Array.from({ length: daysInMonth(annivMonth, annivYear) }, (_, i) => (
                  <option key={i} value={String(i+1)}>{i+1}</option>
                ))}
              </select>
              <input
                type="text"
                inputMode="numeric"
                value={annivYear}
                onChange={e => setAnnivYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="Year"
                style={{ flex: 1, minWidth: 0, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem' }}
              />
            </div>
          </div>
        )}

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '0.3rem' }}>Remind me via</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {[['sms', 'Text'], ['email', 'Email'], ['both', 'Both']].map(([val, label]) => (
              <button key={val} type="button" onClick={() => setForm({ ...form, notify_pref: val })} style={{ flex: 1, fontSize: '0.8rem', padding: '0.35rem', background: (form.notify_pref || 'both') === val ? '#2c2c2c' : '#f0ebe6', color: (form.notify_pref || 'both') === val ? '#fff' : '#5D4037', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>{label}</button>
            ))}
          </div>
        </div>

        <button
          onClick={saveContact}
          disabled={saving || (guidedStep > 0 ? !form.first_name.trim() : (!form.first_name.trim() && !effectiveRelationship()))}
          style={{ width: '100%', opacity: (guidedStep > 0 ? form.first_name.trim() : (form.first_name.trim() || effectiveRelationship())) ? 1 : 0.5 }}
        >
          {saving ? 'Saving...' : 'Add This Person'}
        </button>
      </div>

      {msg === 'UPGRADE' ? (
        <div style={{ background: '#fdf6ee', border: '1px solid #d4a96a', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1rem' }}>
          <div style={{ fontWeight: '600', color: '#5D4037', fontSize: '0.88rem', marginBottom: '0.25rem' }}>You've reached the 25-contact limit</div>
          <div style={{ fontSize: '0.82rem', color: '#7a5c45', marginBottom: '0.6rem' }}>Upgrade to Premium for unlimited contacts — $20/year.</div>
          <a href="/upgrade" style={{ display: 'inline-block', background: '#5D4037', color: '#fff', padding: '0.45rem 1rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600', textDecoration: 'none' }}>Go Premium →</a>
        </div>
      ) : msg ? (
        <div className="msg err" style={{ marginBottom: '1rem' }}>{msg}</div>
      ) : null}

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <button
          onClick={() => {
            if (guidedStep > 0) { setGuidedStep(guidedStep - 1); resetForm(); }
            else { setShowSplash(true); resetForm(); }
          }}
          style={{ background: '#999', flex: 1 }}
        >
          Back
        </button>
        <button onClick={nextGuidedStep} style={{ background: guidedStep < GUIDED_STEPS.length - 1 ? 'transparent' : '#5D4037', color: guidedStep < GUIDED_STEPS.length - 1 ? '#5D4037' : '#fff', border: guidedStep < GUIDED_STEPS.length - 1 ? '2px solid #5D4037' : 'none', flex: 2 }}>
          {guidedStep < GUIDED_STEPS.length - 1 ? 'Next' : (addedContacts.length > 0 ? 'Next: Holidays →' : 'Skip for now')}
        </button>
      </div>

      <div style={{ borderTop: '1px solid #D7CCC8', paddingTop: '1.25rem', textAlign: 'center', marginTop: '0.5rem' }}>
        <button onClick={() => setMode('file')} style={{ background: '#F5F0EB', color: '#5D4037', fontSize: '1rem', fontWeight: '600', padding: '0.75rem 1.25rem', border: '2px solid #5D4037', borderRadius: '8px', cursor: 'pointer', lineHeight: '1.4' }}>
          Already have birthdays in your contacts? Import from vCard (Mac/iOS, Outlook, etc.) or CSV template instead.
        </button>
      </div>
    </div>
  );
}
